export const GATEWAY_APP_ID = "gateway";

export const LEGACY_GATEWAY_APP_ID = "redirect";

export const CLOUD_APP_ID = "mintly-cloud";

export const COURSES_APP_ID = "courses";

export const LIVE_STREAM_APP_ID = "live-stream";

export const CONSULTING_APP_ID = "consulting";

export const CREATOR_REWARDS_APP_ID = "creator-rewards";

/** Product unlocks the Consulting app (chat + LiveKit calls). */
export const productUnlocksConsulting = (product) => {
  if (!product) return false;
  const apps = product.apps?.length
    ? product.apps
    : product.appId
      ? [{ appId: product.appId }]
      : [];
  return apps.some((a) => normalizeInstalledAppId(a.appId) === CONSULTING_APP_ID);
};

export const PRODUCT_EXCLUDED_APP_IDS = ["creator-rewards"];

/** Apps that can be sold, but only as a product of their own — never alongside other apps. */

export const SOLD_ALONE_APP_IDS = [GATEWAY_APP_ID];



export const isAppSoldAlone = (appId) =>

  SOLD_ALONE_APP_IDS.includes(normalizeInstalledAppId(appId));



/** A catalog app an owner can sell as a product — Creator Rewards and unreleased apps can't be. */

export const canSellAppAsProduct = (app) =>

  !!app && !app.comingSoon && !PRODUCT_EXCLUDED_APP_IDS.includes(normalizeInstalledAppId(app.id));



/** Catalog app id → Mint Space window nav id */

const APP_ID_TO_NAV = {

  [CREATOR_REWARDS_APP_ID]: "campaigns",

  [GATEWAY_APP_ID]: "gateway",

  [LEGACY_GATEWAY_APP_ID]: "gateway",

  [CLOUD_APP_ID]: "cloud",

  [COURSES_APP_ID]: "courses",

  [LIVE_STREAM_APP_ID]: "live-stream",

  [CONSULTING_APP_ID]: "consulting",

};



const NAV_TO_APP_ID = {

  campaigns: CREATOR_REWARDS_APP_ID,

  gateway: GATEWAY_APP_ID,

  cloud: CLOUD_APP_ID,

  courses: COURSES_APP_ID,

  "live-stream": LIVE_STREAM_APP_ID,

  consulting: CONSULTING_APP_ID,

};



export const mintSpaceAppIdToNav = (appId) => APP_ID_TO_NAV[appId] || null;

export const mintSpaceNavToAppId = (navId) => NAV_TO_APP_ID[navId] || null;



export const normalizeInstalledAppId = (id) =>

  id === LEGACY_GATEWAY_APP_ID ? GATEWAY_APP_ID : id;



/** Normalize legacy string entries to `{ instanceId, appId, displayName, visibility }`. */

export const normalizeInstalledApps = (raw = [], mintSpace = null) => {

  if (!Array.isArray(raw)) return [];

  return raw

    .map((entry, idx) => {

      if (typeof entry === "string") {

        const appId = normalizeInstalledAppId(entry);

        const legacyVis = mintSpace ? getMintSpaceAppVisibility(mintSpace, appId) : "private";

        return {

          instanceId: `${appId}-${idx}`,

          appId,

          displayName: "",

          visibility: appId === CREATOR_REWARDS_APP_ID ? "public" : legacyVis,

        };

      }

      if (entry && typeof entry === "object" && entry.appId) {

        const appId = normalizeInstalledAppId(entry.appId);

        const explicitVis =

          entry.visibility === "public" || entry.visibility === "private"

            ? entry.visibility

            : null;

        const legacyVis = mintSpace ? getMintSpaceAppVisibility(mintSpace, appId) : "private";

        return {

          instanceId: String(entry.instanceId || `${appId}-${idx}`),

          appId,

          displayName: String(entry.displayName || "").trim(),

          visibility:

            appId === CREATOR_REWARDS_APP_ID

              ? "public"

              : explicitVis || legacyVis,

          cloudWallpaper: entry.cloudWallpaper,

        };

      }

      return null;

    })

    .filter(Boolean);

};



export const hasMintSpaceApp = (mintSpace, appId) => {

  const instances = normalizeInstalledApps(mintSpace?.installedApps, mintSpace);

  const id = normalizeInstalledAppId(appId);

  if (id === GATEWAY_APP_ID) {

    return instances.some(

      (i) => i.appId === GATEWAY_APP_ID || i.appId === LEGACY_GATEWAY_APP_ID

    );

  }

  return instances.some((i) => i.appId === id);

};



export const resolveAppCatalogEntry = (catalog, appId) =>

  catalog.find((a) => a.id === normalizeInstalledAppId(appId));



export const getMintSpaceAppSetting = (mintSpace, appId) => {

  const id = normalizeInstalledAppId(appId);

  const settings = mintSpace?.appSettings || [];

  return settings.find((s) => normalizeInstalledAppId(s.appId) === id) || null;

};



export const getMintSpaceAppVisibility = (mintSpace, appId) => {

  if (normalizeInstalledAppId(appId) === CREATOR_REWARDS_APP_ID) return "public";

  const setting = getMintSpaceAppSetting(mintSpace, appId);

  return setting?.visibility === "private" ? "private" : "public";

};



export const getMintSpaceAppDisplayName = (mintSpace, appId, fallback = "") => {

  const setting = getMintSpaceAppSetting(mintSpace, appId);

  const custom = (setting?.name || "").trim();

  return custom || fallback;

};



export const getInstanceById = (mintSpace, instanceId) =>

  normalizeInstalledApps(mintSpace?.installedApps, mintSpace).find(

    (i) => i.instanceId === instanceId

  ) || null;



export const getInstanceVisibility = (mintSpace, instance) => {

  if (!instance) return "public";

  if (normalizeInstalledAppId(instance.appId) === CREATOR_REWARDS_APP_ID) return "public";

  if (instance.visibility === "public" || instance.visibility === "private") {

    return instance.visibility;

  }

  return getMintSpaceAppVisibility(mintSpace, instance.appId);

};



/** True if the viewer should see this installed app instance in the sidebar. */

export const canSeeMintSpaceAppInstance = (

  mintSpace,

  instance,

  { isOwner = false, isModerator = false } = {}

) => {

  if (!instance) return false;

  if (getInstanceVisibility(mintSpace, instance) === "private") {

    if (isOwner || isModerator) return true;

    // An app someone holds a product on is theirs to open, private or not.

    return unlockedInstanceIds(mintSpace).has(String(instance.instanceId));

  }

  return true;

};



/** @deprecated Prefer canSeeMintSpaceAppInstance for multi-instance sidebars. */

export const canSeeMintSpaceApp = (

  mintSpace,

  appId,

  { isOwner = false, isModerator = false } = {}

) => {

  if (!hasMintSpaceApp(mintSpace, appId)) return false;

  const instances = normalizeInstalledApps(mintSpace?.installedApps, mintSpace).filter(

    (i) => normalizeInstalledAppId(i.appId) === normalizeInstalledAppId(appId)

  );

  if (instances.length === 0) return false;

  return instances.some((inst) =>

    canSeeMintSpaceAppInstance(mintSpace, inst, { isOwner, isModerator })

  );

};



export const getInstanceDisplayName = (mintSpace, instance, catalogName = "") => {

  const instName = (instance?.displayName || "").trim();

  if (instName) return instName;

  const appName = getMintSpaceAppDisplayName(mintSpace, instance?.appId, catalogName);

  return appName || catalogName || instance?.appId || "";

};



export const instanceNavId = (instance) =>

  `instance-${instance?.instanceId || instance?.appId}`;



export const resolveInstanceFromNavId = (navId, mintSpace) => {

  if (!navId?.startsWith("instance-")) return null;

  const instanceId = navId.slice("instance-".length);

  return normalizeInstalledApps(mintSpace?.installedApps, mintSpace).find(

    (i) => i.instanceId === instanceId

  );

};



export const appNavIdToPageNav = (navId, mintSpace) => {

  const inst = resolveInstanceFromNavId(navId, mintSpace);

  if (inst) return mintSpaceAppIdToNav(inst.appId) || "home";

  return navId;

};



/** First installed instance of an app type (legacy product fallback). */

export const getFirstInstanceOfApp = (mintSpace, appId) => {

  const id = normalizeInstalledAppId(appId);

  return (

    normalizeInstalledApps(mintSpace?.installedApps, mintSpace).find(

      (i) => normalizeInstalledAppId(i.appId) === id

    ) || null

  );

};



/**

 * The installed instances a product unlocks, main app first: the apps it lists, or for a

 * product from before it could hold several, its one instance (older still: the first

 * install of its app).

 */

export const productInstanceIds = (product, mintSpace) => {

  const listed = (product?.apps || [])

    .map((a) => String(a?.appInstanceId || "").trim())

    .filter(Boolean);

  if (listed.length) return [...new Set(listed)];

  const single =

    String(product?.appInstanceId || "").trim() ||

    getFirstInstanceOfApp(mintSpace, product?.appId)?.instanceId;

  return single ? [String(single)] : [];

};



/** The product's main instance — where its buyer lands, and whose look it takes. */

export const resolveProductInstanceId = (product, mintSpace) =>

  productInstanceIds(product, mintSpace)[0] || null;



/** Products the viewer has bought here, as the API reports them. */

export const purchasedProductIds = (mintSpace) =>

  new Set((mintSpace?.purchasedProductIds || []).map(String));





/**

 * Instances the viewer has unlocked by holding a product sold on them — every app of it.

 *

 * A product they bought, or a free one, which every member holds without buying. That is

 * what makes a private app visible, so this decides both what the sidebar lists and

 * which ?nav= the mint space will accept.

 */

export const unlockedInstanceIds = (mintSpace) => {

  const bought = purchasedProductIds(mintSpace);

  const ids = new Set();

  for (const product of mintSpace?.appPricing || []) {

    if (!bought.has(String(product._id)) && !isProductFreeForJoin(product)) continue;

    for (const instanceId of productInstanceIds(product, mintSpace)) ids.add(instanceId);

  }

  return ids;

};



/**
 * The apps a product holds, main app first, as its "View apps inside" folder shows them:
 * the name the owner gave each install, its catalog icon, and the nav id that opens it.
 * Apps no longer installed are left out.
 */
export const productFolderApps = (mintSpace, product, catalog) =>
  productInstanceIds(product, mintSpace)
    .map((instanceId) => getInstanceById(mintSpace, instanceId))
    .filter(Boolean)
    .map((instance) => {
      const app = resolveAppCatalogEntry(catalog, instance.appId);
      if (!app) return null;
      return {
        appId: app.id,
        key: String(instance.instanceId),
        name: getInstanceDisplayName(mintSpace, instance, app.name) || app.name,
        icon: app.icon,
        color: app.color,
        navId: instanceNavId(instance),
      };
    })
    .filter(Boolean);

/** True when this user owns the Mint Space or moderates it. */
export const managesMintSpace = (mintSpace, userId) => {
  if (!mintSpace || !userId) return false;
  const uid = String(userId);
  const ownerId = String(mintSpace.owner?._id || mintSpace.owner || "");
  return ownerId === uid || (mintSpace.moderators || []).some((m) => String(m?._id || m) === uid);
};

/** True when a product does not require payment (free access or zero price). */
export const isProductFreeForJoin = (product) => {
  const accessType = String(product?.accessType || "").trim().toLowerCase();
  const price = Number(product?.price) || 0;
  return accessType === "free" || price <= 0;
};

/** True when joining the mint space itself is free (no join fee). Products are separate. */
export const isMintSpaceFreeToJoin = (mintSpace) => {
  const joinFee = Number(mintSpace?.joinFeeAmount) || 0;
  return joinFee <= 0;
};

/** Label + amount for the members gate (mint space join fee only). */
export const getMintSpaceJoinDisplay = (mintSpace) => {
  const joinFee = Number(mintSpace?.joinFeeAmount) || 0;
  const currency = mintSpace?.joinFeeCurrency || "GEL";

  if (joinFee <= 0) {
    return { isFree: true };
  }

  return { isFree: false, amount: joinFee, currency, source: "joinFee", productId: null };
};

